import React from 'react';
import type { Flight } from '../../types/flight';

export const INITIAL_EMPTY_FLIGHT: Partial<Flight> = {
    date: '',
    flightNumber: '',
    departure: '',
    departureCity: '',
    arrival: '',
    arrivalCity: '',
    airline: '',
    duration: '',
    distance: 0,
    aircraft: '',
    aircraft_reg: '',
    departure_time: '',
    arrival_time: '',
    price: 0,
    tag: '',
    class: 'Economy',
};

/** Prefer real IATA; never take first 3 Cyrillic chars of a city label. */
export function iataOrFallback(code?: string | null, city?: string | null): string {
    const c = (code || '').trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(c)) return c;
    const fromCity = (city || '').toUpperCase().match(/(?<![A-Z0-9])([A-Z]{3})(?![A-Z0-9])/);
    if (fromCity) return fromCity[1];
    return 'XXX';
}

export function parseTime(s: string | undefined): { h: string; m: string } {
    if (!s) return { h: '', m: '' };
    const [h = '', m = ''] = s.split(':');
    return { h, m };
}

/** Keep partial digits while typing — do not zero-pad until save. */
export function formatTime(h: string, m: string): string {
    const hh = h.replace(/\D/g, '').slice(0, 2);
    const mm = m.replace(/\D/g, '').slice(0, 2);
    if (!hh && !mm) return '';
    return `${hh}:${mm}`;
}

export type FlightEditPayload = {
    date: string;
    tag?: string;
    departure: string;
    departureCity: string;
    arrival: string;
    arrivalCity: string;
    airline: string;
    airline_iata?: string;
    flightNumber: string;
    duration: string;
    distance: number;
    aircraft: string;
    aircraft_reg?: string;
    departure_time?: string;
    arrival_time?: string;
    flightClass: string;
    price: number;
    departureSubdivision?: string;
    arrivalSubdivision?: string;
};

export function buildFlightEditPayload(n: Partial<Flight>): FlightEditPayload {
    return {
        date: n.date || '',
        tag: n.tag || undefined,
        departure: iataOrFallback(n.departure, n.departureCity),
        departureCity: n.departureCity || '',
        arrival: iataOrFallback(n.arrival, n.arrivalCity),
        arrivalCity: n.arrivalCity || '',
        airline: n.airline || '-',
        airline_iata: n.airline_iata,
        flightNumber: n.flightNumber || '',
        duration: n.duration || '0:00',
        distance: Number(n.distance) || 0,
        aircraft: n.aircraft || '',
        aircraft_reg: n.aircraft_reg,
        departure_time: n.departure_time || undefined,
        arrival_time: n.arrival_time || undefined,
        flightClass: n.class || 'Economy',
        price: Number(n.price) || 0,
        departureSubdivision: n.departureSubdivision || undefined,
        arrivalSubdivision: n.arrivalSubdivision || undefined,
    };
}

function flightPayloadFromOriginal(flight: Flight): FlightEditPayload {
    return buildFlightEditPayload({
        ...flight,
        class: flight.class ?? (flight as Flight & { flightClass?: string }).flightClass ?? 'Economy',
    });
}

function flightPayloadFieldEqual(
    key: keyof FlightEditPayload,
    left: FlightEditPayload,
    right: FlightEditPayload,
): boolean {
    const a = left[key];
    const b = right[key];
    if (typeof a === 'number' || typeof b === 'number') {
        return Number(a) === Number(b);
    }
    return (a ?? '') === (b ?? '');
}

export function flightEditDelta(
    original: Flight,
    draft: Partial<Flight>,
): Partial<FlightEditPayload> {
    const before = flightPayloadFromOriginal(original);
    const after = buildFlightEditPayload(draft);
    const delta: Partial<FlightEditPayload> = {};
    (Object.keys(before) as (keyof FlightEditPayload)[]).forEach((key) => {
        if (!flightPayloadFieldEqual(key, before, after)) {
            delta[key] = after[key] as never;
        }
    });
    return delta;
}

export function hasFlightEditChanges(original: Flight, draft: Partial<Flight>): boolean {
    return Object.keys(flightEditDelta(original, draft)).length > 0;
}

export function TimeHmInputs({
    value,
    onChange,
    hourPlaceholder = 'Ч',
    minutePlaceholder = 'М',
}: {
    value: string | undefined;
    onChange: (next: string) => void;
    hourPlaceholder?: string;
    minutePlaceholder?: string;
}) {
    const { h, m } = parseTime(value);
    const box =
        'w-9 h-9 px-0.5 border border-sky-200/80 dark:border-slate-600 rounded-lg text-center bg-white/80 dark:bg-slate-800/80 dark:text-white focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400 font-mono text-sm tabular-nums';
    return (
        <div className="flex items-center gap-0.5">
            <input
                type="text"
                inputMode="numeric"
                maxLength={2}
                value={h}
                placeholder={hourPlaceholder}
                aria-label={hourPlaceholder}
                onChange={e => onChange(formatTime(e.target.value, m))}
                className={box}
            />
            <span className="text-gray-400 select-none">:</span>
            <input
                type="text"
                inputMode="numeric"
                maxLength={2}
                value={m}
                placeholder={minutePlaceholder}
                aria-label={minutePlaceholder}
                onChange={e => onChange(formatTime(h, e.target.value))}
                className={box}
            />
        </div>
    );
}

export const FLIGHT_CLASS_VALUES = ['Economy', 'Premium Economy', 'Business', 'First'] as const;

export function flightClassLabel(value: string, t: Record<string, string>): string {
    switch (value) {
        case 'Economy': return t.economy ?? 'Economy';
        case 'Premium Economy': return t.premiumEconomy ?? 'Premium Economy';
        case 'Business': return t.business ?? 'Business';
        case 'First': return t.first ?? 'First';
        default: return value;
    }
}
